'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const HowItWorksSection = () => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    const steps = [
        {
            id: "01",
            title: "Assess",
            desc: "Take our comprehensive AI-driven assessment to establish your baseline skills and identify growth areas.",
            icon: "ChartBarIcon",
            gradient: "from-primary to-accent",
            glow: "primary"
        },
        {
            id: "02",
            title: "Learn",
            desc: "Follow a personalized learning path tailored to your specific career goals and industry requirements.",
            icon: "AcademicCapIcon",
            gradient: "from-accent to-success",
            glow: "accent"
        },
        {
            id: "03",
            title: "Practice",
            desc: "Engage with real-world projects and mock interviews to build confidence and practical expertise.",
            icon: "CodeBracketIcon",
            gradient: "from-success to-primary",
            glow: "success"
        },
        {
            id: "04",
            title: "Succeed",
            desc: "Get connected with top recruiters and land your dream job offer with our exclusive network.",
            icon: "TrophyIcon",
            gradient: "from-primary via-accent to-success",
            glow: "primary"
        }
    ];

    return (
        <section ref={sectionRef} className="py-32 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px] animate-pulse" />
                <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-[120px] animate-pulse delay-1000" />
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Section Header */}
                <div
                    className={`text-center mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                        }`}
                >
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-6">
                        <Icon name="SparklesIcon" size={20} className="text-primary" />
                        <span className="text-sm font-medium text-text-secondary">Simple & Effective</span>
                    </div>

                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                        How It <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-success">Works</span>
                    </h2>

                    <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
                        Your journey to career success in four simple, proven steps designed by industry experts.
                    </p>
                </div>

                {/* Steps Grid */}
                <div className="relative">
                    {/* Connecting Path - Desktop */}


                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {steps.map((step, idx) => (
                            <div
                                key={idx}
                                className={`group relative transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                                    }`}
                                style={{ transitionDelay: `${idx * 200}ms` }}
                            >
                                {/* Card */}
                                <div className="relative h-full p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-500 hover:border-white/20 hover:shadow-2xl hover:-translate-y-2">
                                    {/* Glow Effect on Hover */}
                                    <div className={`absolute -inset-0.5 bg-gradient-to-r ${step.gradient} opacity-0 group-hover:opacity-10 blur-xl transition-opacity duration-500 rounded-2xl -z-10`} />

                                    {/* Step Number Badge */}
                                    <div className="absolute -top-4 -right-4">
                                        <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${step.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                                            <span className="text-2xl font-bold text-white">{step.id}</span>
                                        </div>
                                    </div>

                                    {/* Icon Circle */}
                                    <div className="mb-6 relative">
                                        <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${step.gradient} p-[2px] group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                                            <div className="w-full h-full rounded-2xl bg-background flex items-center justify-center">
                                                <Icon name={step.icon} size={36} className="text-white" />
                                            </div>
                                        </div>

                                        {/* Pulsing glow */}
                                        <div className={`absolute inset-0 bg-gradient-to-br ${step.gradient} blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500 rounded-2xl`} />
                                    </div>

                                    {/* Content */}
                                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-primary transition-colors duration-300">
                                        {step.title}
                                    </h3>

                                    <p className="text-text-secondary leading-relaxed group-hover:text-text-primary/80 transition-colors duration-300">
                                        {step.desc}
                                    </p>

                                    {/* Arrow Indicator (except last) */}
                                    {idx < steps.length - 1 && (
                                        <div className="hidden lg:block absolute top-1/2 -right-6 transform -translate-y-1/2">
                                            <Icon
                                                name="ArrowRightIcon"
                                                size={24}
                                                className={`text-primary opacity-30 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-500`}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* CTA Section */}
                <div
                    className={`mt-20 text-center transition-all duration-1000 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                        }`}
                >
                    <div className="inline-flex flex-col sm:flex-row items-center gap-4">
                        <a
                            href="/for-students/signup"
                            className="group relative px-8 py-4 rounded-xl overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-primary via-accent to-success opacity-90 group-hover:opacity-100 transition-opacity duration-300" />
                            <div className="absolute inset-0 bg-[url('/noise.png')] opacity-10 mix-blend-overlay" />
                            <div className="absolute -inset-full group-hover:inset-full bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 transition-all duration-700 ease-in-out" />
                            <span className="relative flex items-center gap-2 text-base font-bold text-white tracking-wide">
                                Start Your Journey
                                <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform" />
                            </span>
                        </a>

                        <a
                            href="/about"
                            className="px-8 py-4 rounded-xl border-2 border-white/10 text-white font-medium hover:bg-white/5 hover:border-white/20 transition-all duration-300"
                        >
                            Learn More
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HowItWorksSection;
